import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import { getMyPickupAddress, saveMyPickupAddress } from '../api';
import AddressAutocomplete from './AddressAutocomplete';

// The rental owner's own pickup / business address. Stored on RentalUser.address and used as
// the pickup end of every OUTBOUND delivery ticket for their listings (and the drop end of a
// RETURN ticket) - see
// Docs/requirements/jewelry_rental/delivery/delivery_ticket_pickup_drop_distance.md.
// Until it's set, tickets fall back to the listing's free-text location, which is usually too
// vague for an accurate Maps distance - hence the nudge when line1 is empty.

const EMPTY = { line1: '', line2: '', area: '', city: '', state: '', pincode: '', lat: null, lng: null, placeId: '', formatted: '' };

export default function PickupAddressCard({ className = '' }) {
  const [address, setAddress] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const a = await getMyPickupAddress();
      setAddress({ ...EMPTY, ...(a || {}) });
    } catch {
      setError('Could not load your pickup address.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      const next = await saveMyPickupAddress(address);
      setAddress({ ...EMPTY, ...(next || {}) });
      setSaved(true);
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to save your pickup address.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner animation="border" size="sm" role="status" />;

  return (
    <Card className={className}>
      <Card.Body>
        <Card.Title as="h6">Pickup address</Card.Title>
        <p className="text-muted small mb-2">
          Where delivery staff collect your items from. Shown on every delivery ticket, and used
          to work out the delivery distance.
        </p>
        {!address.line1 && (
          <Alert variant="warning" className="py-2 small">
            No pickup address set — delivery tickets currently use each listing's location text,
            which may not be precise enough for an accurate distance.
          </Alert>
        )}
        {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}
        {saved && <Alert variant="success" className="py-2 small">Pickup address saved.</Alert>}
        <Form onSubmit={handleSubmit} style={{ maxWidth: 520 }}>
          <AddressAutocomplete
            value={address}
            onChange={(a) => { setAddress(a); setSaved(false); }}
            showState
            required
            idPrefix="pickup"
          />
          <Button size="sm" type="submit" disabled={saving}>
            {saving ? 'Saving…' : 'Save pickup address'}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
}
